import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent, createBlock as _createBlock, Fragment as _Fragment, normalizeClass as _normalizeClass, withCtx as _withCtx, createElementVNode as _createElementVNode, withModifiers as _withModifiers, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["src", "alt"]
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 1,
  class: "row no-wrap"
}
const _hoisted_4 = ["accept"]
const _hoisted_5 = ["src", "alt"]
const _hoisted_6 = { class: "text-body1 ellipsis full-width" }

import {
  Camera,
  CameraResultType,
  CameraSource,
  ImageOptions,
} from "@src-capacitor/@capacitor/camera"
import { Platform } from "quasar"
import { getCommonDB } from "src/db/databases"
import { useUserStore } from "src/stores/userStore"
import { Log } from "src/util/classes/log"
import { IMAGE_FILE_MIME_TYPES, MAX_UPLOAD_PROFILE_PIC_SIZE } from "src/util/constants/files"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import { confirmationDialog } from "src/util/functions/dialogs"
import {
  base64ToBlob,
  determineProfilePictureColor,
  extractInitials,
} from "src/util/functions/misc"
import {
  showErrorNotification,
  showMaxProfilePicSizeExceededNotification,
} from "src/util/functions/notifications"
import { getUserAvatarData, saveUserAvatar } from "src/util/functions/user"
import { deleteCurrentUserImage, postCurrentUserImage } from "src/util/http/springboard/user"
import { computed, onMounted, onUnmounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxCurrentUserDisplay',
  props: {
  changePicture: {
    type: Boolean,
    default: false,
  },

  small: {
    type: Boolean,
    default: false,
  },

  avatarOnly: {
    type: Boolean,
    default: false,
  },

  disable: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {

const props = __props

//endregion

//region Variables

const userStore = useUserStore()
const fullName = computed(() => userStore.getFullName)
const { t } = useI18n()
const webChangeProfilePicture = ref<HTMLElement | null>(null)

const nameClass = computed(() => {
  return {
    "text-h5": props.small,
    "line-clamp": props.small,
    "text-h4": !props.small,
    ellipsis: !props.small,
  }
})

const avatarClass = computed(() => {
  return { "cursor-pointer": props.changePicture }
})
const avatarSize = computed(() => {
  if (props.avatarOnly) {
    return "32px"
  } else if (props.small) {
    return "48px"
  } else {
    return "80px"
  }
})
const initialsClass = computed(() =>
  props.small ? "sfx-current-user-display__initials-small" : "sfx-current-user-display__initials"
)
const userInfoClass = computed(() => {
  return {
    ellipsis: !props.small,
    "sfx-current-user-display__username": props.small,
  }
})

const userProfilePicture = ref<string>()
const profilePictureColor = computed<string>(() =>
  initials.value ? determineProfilePictureColor(initials.value) : "grey"
)
const initials = computed(() => extractInitials(userStore.getFullName))
const disabledClass = computed(() => {
  return { disabled: props.disable }
})

//endregion

//region Lifecycle

onMounted(async () => {
  webChangeProfilePicture.value?.addEventListener("change", handleWebFiles, false)
})

onUnmounted(async () => {
  webChangeProfilePicture.value?.addEventListener("change", handleWebFiles, false)
})

//endregion

//region Functions

async function removeProfilePicture() {
  // We don't have the ability to change, so don't bother
  if (!props.changePicture || !userProfilePicture.value) return

  const result = await confirmationDialog({
    title: t("sfxUserDisplay.image_delete_dialog_title"),
    content: t("sfxUserDisplay.image_delete_dialog_content"),
  })

  if (!result) return

  try {
    await deleteCurrentUserImage()
    const db = await getCommonDB()
    const userDocument = await db.user_avatar.findOne(userStore.id).exec()
    await userDocument?.remove()
    userProfilePicture.value = undefined
    userStore.profilePicture = userProfilePicture.value
  } catch (error) {
    Log.debug({ message: "Error removing profile picture", data: { error } })
  }
}

async function changeProfilePicture() {
  // We don't have the ability to change, so don't bother
  if (!props.changePicture) return

  if (Platform.is.capacitor) {
    await handleCapacitorFiles()
  } else {
    // Trigger input file upload
    webChangeProfilePicture.value?.click()
  }
}

/**
 * Listener event for detecting changes in input type "file".
 * If a valid file is selected, it is passed off to the user store.
 *
 * @param event Change event from _input type="file"_
 */
async function handleWebFiles(event: Event) {
  const fileList = (event.target as HTMLInputElement)?.files
  if (fileList && fileList.length > 0) {
    // We cannot enforce browsers of the file type, so we check if it is valid
    if (IMAGE_FILE_MIME_TYPES.includes(fileList[0].type)) {
      await handleImageUpload(fileList[0])
    } else {
      const message = `${t("sfxUserDisplay.image_file_support")} ${t(
        "sfxUserDisplay.file_type_provided"
      )}:${fileList[0].type}.`

      showErrorNotification(message)
    }
  }
}

/**
 * Show a bottom sheet with options to take a photo or select file from gallery.
 * The file is then passed off to the user store.
 */
async function handleCapacitorFiles() {
  const imageOptions = await getCapacitorImageOptions()

  // User has given us some permissions
  if (imageOptions) {
    // TODO: We need to handle user cancel events. This is not currently available in Capacitor
    // https://github.com/ionic-team/capacitor/issues/5166
    // If issue is not resolved before production, configure Sentry to ignore it

    const selectedImage = await Camera.getPhoto(imageOptions)

    if (selectedImage && selectedImage.base64String) {
      const blob = await base64ToBlob(selectedImage.base64String, "image/" + selectedImage.format)
      const fileName = userStore.id + "." + selectedImage.format
      const file = new File([blob], fileName, {
        type: blob.type,
      })
      await handleImageUpload(file)
    }
  } else {
    showErrorNotification(t("sfxUserDisplay.permission_camera_storage"))
  }
}

async function handleImageUpload(image: File) {
  try {
    if (image.size > MAX_UPLOAD_PROFILE_PIC_SIZE) {
      return await showMaxProfilePicSizeExceededNotification()
    }
    const imageOnServer = await postCurrentUserImage({}, image)
    if (imageOnServer) {
      await saveUserAvatar(userStore.id, image)
      userProfilePicture.value = imageOnServer.image
      userStore.profilePicture = userProfilePicture.value
    }
  } catch (error) {
    Log.debug({ message: "Error changing profile picture", data: { error } })
  }
}

/**
 * Checks and asks if needed for permissions for both Camera and Storage.
 * Depending on what permissions have been granted, return appropriate
 * ImageOptions or undefined if none.
 */
async function getCapacitorImageOptions(): Promise<ImageOptions | undefined> {
  const imageOptions: ImageOptions = {
    resultType: CameraResultType.Base64,
    promptLabelHeader: t("sfxUserDisplay.image_select"),
    promptLabelPhoto: t("sfxUserDisplay.image_select_gallery"),
    promptLabelPicture: t("sfxUserDisplay.image_select_take_photo"),
  }

  const permissions = await Camera.requestPermissions({ permissions: ["camera", "photos"] })

  // Most common case would be user giving us permissions for both
  if (permissions.camera === "granted" && permissions.photos === "granted") {
    return imageOptions
  } else if (permissions.camera === "granted") {
    imageOptions.source = CameraSource.Camera
  } else if (permissions.photos === "granted") {
    imageOptions.source = CameraSource.Photos
  } else {
    return undefined
  }

  return imageOptions
}

//endregion

//region Setup Code

const unwatchUserId = watch(
  () => userStore.id,
  async (userId) => {
    // This component can sometimes load before the user data is populated
    if (userId) {
      userProfilePicture.value = await getUserAvatarData(userStore.id)
      // We already have the user ID, and it cannot change without doing a sign-out, so no need to listen  further.
      unwatchUserId()
    }
  },
  { immediate: true }
)

watch(
  () => userStore.profilePicture,
  (newVal) => {
    userProfilePicture.value = newVal || ""
  }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_avatar = _resolveComponent("q-avatar")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _directive_touch_hold = _resolveDirective("touch-hold")!

  return (props.avatarOnly)
    ? (_openBlock(), _createBlock(_component_q_avatar, {
        key: 0,
        class: _normalizeClass(["sfx-current-user-display", disabledClass.value]),
        size: avatarSize.value,
        color: profilePictureColor.value
      }, {
        default: _withCtx(() => [
          (userProfilePicture.value)
            ? (_openBlock(), _createElementBlock("img", {
                key: 0,
                src: userProfilePicture.value,
                alt: _ctx.$t('sfxUserDisplay.profile_picture')
              }, null, 8, _hoisted_1))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (initials.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(initials.value), 1))
                  : (_openBlock(), _createBlock(_component_q_icon, {
                      key: 1,
                      name: _unref(Icons).USER_PROFILE
                    }, null, 8, ["name"]))
              ], 64))
        ]),
        _: 1
      }, 8, ["class", "size", "color"]))
    : (_openBlock(), _createElementBlock("div", _hoisted_3, [
        _withDirectives((_openBlock(), _createBlock(_component_q_avatar, {
          class: _normalizeClass(["sfx-current-user-display", [avatarClass.value, disabledClass.value]]),
          size: avatarSize.value,
          onClick: changeProfilePicture,
          color: profilePictureColor.value
        }, {
          default: _withCtx(() => [
            _createElementVNode("input", {
              type: "file",
              id: "changeProfilePictureInput",
              accept: _unref(IMAGE_FILE_MIME_TYPES),
              ref_key: "webChangeProfilePicture",
              ref: webChangeProfilePicture,
              hidden: ""
            }, null, 8, _hoisted_4),
            (userProfilePicture.value)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 0,
                  src: userProfilePicture.value,
                  alt: _ctx.$t('sfxUserDisplay.profile_picture')
                }, null, 8, _hoisted_5))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  (initials.value)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: _normalizeClass(initialsClass.value)
                      }, _toDisplayString(initials.value), 3))
                    : (_openBlock(), _createBlock(_component_q_icon, {
                        key: 1,
                        name: _unref(Icons).USER_PROFILE
                      }, null, 8, ["name"]))
                ], 64)),
            (userProfilePicture.value && props.changePicture)
              ? (_openBlock(), _createBlock(_component_q_btn, {
                  key: 2,
                  icon: _unref(Icons).CLOSE,
                  size: "xs",
                  color: "negative",
                  "text-color": "white",
                  unelevated: "",
                  round: "",
                  class: "sfx-current-user-display__close_button",
                  onClick: _withModifiers(removeProfilePicture, ["stop"]),
                  disable: props.disable
                }, null, 8, ["icon", "disable"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["class", "size", "color"])), [
          [
            _directive_touch_hold,
            removeProfilePicture,
            void 0,
            { mouse: true }
          ]
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(["column q-ml-md justify-center", [userInfoClass.value, disabledClass.value]])
        }, [
          (props.small)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: _normalizeClass(["full-width block ellipsis", nameClass.value])
              }, _toDisplayString(fullName.value || _unref(userStore).email), 3))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createElementVNode("span", {
                  class: _normalizeClass(["full-width ellipsis", nameClass.value])
                }, _toDisplayString(fullName.value), 3),
                _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(userStore).email), 1)
              ], 64))
        ], 2)
      ]))
}
}

})